import React from 'react';
import { useTranslation } from '../hooks/useTranslation';
import PlatformButton from './PlatformButton';
import PlatformText from './PlatformText';
import PlatformCard from './PlatformCard';
import { getPlatformType, getPlatformStyles } from '../utils/platformUtils';

const ErrorModal = ({ 
  isOpen, 
  onClose, 
  error, 
  title, 
  onRetry, 
  showRetry = true,
  variant = 'error' // 'error', 'warning', 'critical'
}) => {
  const { t } = useTranslation('errors');
  const platformType = getPlatformType();

  if (!isOpen) return null;

  const getVariantStyles = () => {
    switch (variant) {
      case 'warning':
        return {
          backgroundColor: 'var(--warning-color)',
          borderColor: 'var(--warning-hover)',
          icon: '⚠️',
          textColor: 'white',
          titleColor: 'white'
        };
      case 'critical':
        return {
          backgroundColor: 'var(--danger-color)',
          borderColor: 'var(--danger-hover)',
          icon: '🚨',
          textColor: 'white',
          titleColor: 'white'
        };
      default: // error
        return {
          backgroundColor: 'var(--danger-color)',
          borderColor: 'var(--danger-hover)',
          icon: '❌',
          textColor: 'white',
          titleColor: 'white'
        };
    }
  };

  const variantStyles = getVariantStyles();

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px'
      }}
      onClick={handleBackdropClick}
    >
      <PlatformCard
        style={{
          maxWidth: '500px',
          width: '100%',
          backgroundColor: variantStyles.backgroundColor,
          border: `2px solid ${variantStyles.borderColor}`,
          borderRadius: platformType === 'ios' ? '12px' : '8px',
          padding: '24px',
          boxShadow: platformType === 'ios' 
            ? '0 4px 16px rgba(0, 0, 0, 0.15)' 
            : '0 4px 8px rgba(0, 0, 0, 0.3)',
          position: 'relative'
        }}
      >
        {/* Error Icon */}
        <div style={{ 
          textAlign: 'center', 
          fontSize: '48px', 
          marginBottom: '16px' 
        }}>
          {variantStyles.icon}
        </div>

        {/* Error Title */}
        <PlatformText 
          variant="h3" 
          align="center" 
          style={{ 
            color: variantStyles.titleColor,
            marginBottom: '16px',
            fontWeight: '600'
          }}
        >
          {title || t('general.unexpectedError')}
        </PlatformText>

        {/* Error Message */}
        <PlatformText 
          variant="body" 
          align="center" 
          style={{ 
            color: variantStyles.textColor,
            marginBottom: '24px',
            lineHeight: '1.5',
            wordBreak: 'break-word'
          }}
        >
          {error}
        </PlatformText>

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {showRetry && onRetry && (
            <PlatformButton 
              onClick={onRetry}
              variant="primary"
              style={{
                backgroundColor: 'white',
                color: variantStyles.backgroundColor,
                minWidth: '120px',
                border: '2px solid white'
              }}
            >
              <PlatformText translationKey="general.retry" namespace="errors" />
            </PlatformButton>
          )}

          <PlatformButton 
            onClick={onClose}
            variant="outline"
            style={{
              borderColor: 'white',
              color: 'white',
              minWidth: '120px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            }}
          >
            <PlatformText translationKey="general.close" namespace="errors" />
          </PlatformButton>
        </div>
      </PlatformCard>
    </div>
  );
};

export default ErrorModal;
